/*
 * CHI-20: /events/:slug/tickets — one generic page for every internally
 * Stripe-paid, non-special event (spec /workspace/chi/ux/chi-20-ticket-page.md).
 * Logic: hooks/useTicketPurchase. Presentation: components/tickets/TicketPurchaseParts.
 * Payment errors are handled on Stripe's hosted page; there is no error page here.
 */
import { useEffect } from "react";
import { Link, useParams } from "wouter";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { getLoginPath } from "@/const";
import { getEventBySlug } from "@/lib/eventsData";
import type { CHIEvent } from "@/lib/eventsData";
import { useEventTitle } from "@/hooks/useEventTitle";
import { trpc } from "@/lib/trpc";
import { inputStyle } from "@/components/memberForm/fields";
import { shouldUseTicketsPage } from "@/lib/eventRegistrationFlow";
import { myTicketsTabHref, ticketsCheckoutEnabled, VIEW_MY_TICKETS_LABEL } from "@/lib/eventCta";
import { useTicketPurchase } from "@/hooks/useTicketPurchase";
import { Ticket as TicketIcon } from "lucide-react";
// CompleteTicketPurchase (temporary)
import { useMyTicketsForEvent } from "@/hooks/useMyTicketsForEvent";
import { completePurchaseCart, completePurchaseEligibleFrom } from "@/components/CompleteTicketPurchase";
import { BUYER_ALREADY_TICKETED_MESSAGE, CHECKOUT_DISABLED_MESSAGE, CHECKOUT_RATE_LIMITED_MESSAGE, OTP_RESEND_SECONDS } from "@/lib/ticketingApi";
import {
  BuyerCard, ContinueCta, formatCents, DraftRestoredBanner, EventSummaryHeader, GuestCard, HoldExpiredNotice, HoldTimer, StruckPrice,
  OrderSummary, OtpInline, PageAlert, PersonFields, PromotionCodeEntry, TICKET_PAGE_CSS, TicketStepper,
} from "@/components/tickets/TicketPurchaseParts";

export default function EventTickets() {
  const { slug = "" } = useParams<{ slug: string }>();
  const auth = useOptionalAuth();
  const isLoggedIn = Boolean(auth?.isLoggedIn);
  const staticEvent = getEventBySlug(slug);
  const catalogQuery = trpc.events.publicCatalog.useQuery(undefined, { enabled: Boolean(slug), retry: false });
  const managedEvent = ((catalogQuery.data ?? []) as CHIEvent[]).find(item => item.slug === slug);
  const event = managedEvent ?? staticEvent;
  const catalogTitle = useEventTitle(slug);
  const pricingPolicyQuery = trpc.events.publicPricingPolicy.useQuery({ eventSlug: slug }, { enabled: Boolean(slug), retry: false });
  const eligible = shouldUseTicketsPage({ pricingPolicy: pricingPolicyQuery.data, isSpecialEvent: event?.isSpecialEvent === true });
  const t = useTicketPurchase(slug, auth?.user, isLoggedIn);
  // CompleteTicketPurchase (temporary): same boolean as the button.
  const cpTickets = useMyTicketsForEvent(slug, isLoggedIn);
  const cp = completePurchaseCart({ eligible: isLoggedIn && completePurchaseEligibleFrom(cpTickets), accountEmail: auth?.user?.email });
  // Locked row 1 always carries the account email (also when prefill is off or the draft restored another).
  const accountEmail = auth?.user?.email?.trim() ?? "";
  useEffect(() => {
    if (cp.lockBuyerEmail && t.rows[0] && t.rows[0].email !== accountEmail) t.updateRow(0, "email", accountEmail);
  }, [cp.lockBuyerEmail, accountEmail, t.rows[0]?.email]);
  const notEligible = pricingPolicyQuery.data !== undefined && !eligible;
  /*
   * CHI-20 kill switch, checked up front (the server's tickets_checkout_disabled refusal stays as
   * the backstop). Until the policy has loaded nothing purchasable renders, so the form never
   * flashes before the message. A failed policy read fails closed (sales not open).
   */
  const policyLoading = pricingPolicyQuery.data === undefined && !(pricingPolicyQuery as { isError?: boolean }).isError;
  const salesNotOpen = !policyLoading && !notEligible && !ticketsCheckoutEnabled(pricingPolicyQuery.data);
  const guestErrorCount = t.emailRecognition.status === "guest" && t.guestFieldsSubmitted
    ? [t.guestProfileErrors.companyName, t.guestProfileErrors.jobTitle, t.guestProfileErrors.phone].filter(Boolean).length
    : 0;
  const errorCount = (t.errors ? t.errors.reduce((n, e) => n + (e.name ? 1 : 0) + (e.email ? 1 : 0), 0) : 0) + guestErrorCount;

  async function onContinue(e?: React.FormEvent) {
    e?.preventDefault();
    const n = await t.submit();
    if (n > 0 && typeof document !== "undefined") {
      const first = document.querySelector<HTMLInputElement>(".chi-ticket-page [aria-invalid='true']");
      first?.scrollIntoView?.({ block: "center" });
      first?.focus();
    }
  }

  const salesClosed = t.checkoutBlock === "checkout_disabled";
  const ctaState: "idle" | "disabled" | "loading" = t.submitting ? "loading" : salesClosed || t.hold || t.holdPaid || !t.verified || t.soldOut || !t.quote || t.quoteUnavailable || t.promotionLoading || t.promotionNeedsAction ? "disabled" : "idle";
  const ctaReason = salesClosed ? CHECKOUT_DISABLED_MESSAGE : t.holdPaid ? "Payment was confirmed in another tab." : t.hold ? "A checkout is already open. Return to that tab or cancel this order." : t.quoteUnavailable ? "Tickets aren't available right now." : t.soldOut ? "Sold out" : t.promotionLoading ? "Checking promotion code..." : t.promotionNeedsAction ? "Apply or remove your promotion code to continue." : !t.verified ? "Verify your email to continue." : !t.quote ? "Loading..." : undefined;
  const cta = <ContinueCta state={ctaState} reason={ctaReason} onClick={() => undefined} />;
  const holdSlot = t.hold ? (
    <HoldTimer
      orderId={t.hold.orderId}
      expiresAt={t.hold.expiresAt}
      serverNow={t.hold.serverNow}
      receivedAt={t.hold.receivedAt}
      status={t.hold.status}
      action={t.holdAction}
      message={t.holdMessage}
      paid={t.holdPaid}
      restartRequired={t.holdRestartRequired}
      onExpire={t.onHoldExpire}
      onRenew={t.renewHold}
      onCancel={t.cancelHold}
    />
  ) : null;
  const q = t.quote;
  const promoEndText = q?.promoEndsAt ? formatPromoEnd(q.promoEndsAt) : null;
  const promoOn = Boolean(q?.promoActive && q.fullPriceCents > q.unitPriceCents);
  const promoChip = promoOn ? `Promo price${promoEndText ? ` · ends ${promoEndText}` : ""}` : null;
  const promoEnded = Boolean(q && !q.promoActive && q.promoEndsAt && Date.parse(q.promoEndsAt) <= Date.now());
  const ticketHeader = (
    <div className="tp-ticket-header">
      <div className="tp-ticket-title"><TicketIcon size={18} aria-hidden="true" /> Your tickets{promoOn && <span className="tp-ticket-badge">{promoChip?.split(" · ")[0]}</span>}</div>
      <p className="tp-ticket-description" style={{ color: "var(--tp-muted)", fontSize: ".82rem", margin: "6px 0 0" }}>General admission</p>
      {q && <div className="tp-ticket-price">
        <strong><span data-testid="unit-price">{formatCents(q.unitPriceCents, q.currency)}</span> <small style={{ fontSize: ".72rem", fontWeight: 400 }}>per ticket</small></strong>
        {promoOn && <StruckPrice cents={q.fullPriceCents} currency={q.currency} />}
      </div>}
      {!t.soldOut && <TicketStepper value={t.quantity} max={t.maxQuantity} remaining={t.remaining} atMax={t.atMaxNotice} onChange={t.setQuantity} />}
    </div>
  );

  return (
    <div className="chi-ticket-page" style={{ minHeight: "100dvh" }}>
      <style>{TICKET_PAGE_CSS}</style>
      <main style={{ maxWidth: 1060, margin: "0 auto", padding: "12px 20px 40px" }}>
        <div className="tp-checkout-header">
          <Link href={`/events/${slug}`} className="tp-brand-return" aria-label="CHI Alliance — return to event">THE CHI ALLIANCE</Link>
          <span className="tp-secure-badge">Secure checkout</span>
        </div>
        {!event && notEligible ? (
          <PageAlert kind="info">We could not find this event. <a href="/calendar-of-events">See all events</a></PageAlert>
        ) : (
          <EventSummaryHeader title={event?.title ?? catalogTitle ?? "Tickets"} meta={event ? eventMetadata(event) : undefined} soldOut={t.soldOut} />
        )}

        {policyLoading ? (
          <div data-testid="tickets-policy-loading" role="status" aria-live="polite" style={{ color: "#94A3B8", padding: "2rem 0" }}>Loading…</div>
        ) : notEligible ? (
          <PageAlert kind="info">Tickets aren't sold here for this event. <a href={`/events/${slug}`}>Back to event</a></PageAlert>
        ) : salesNotOpen ? (
          <PageAlert kind="info"><span data-testid="tickets-sales-not-open">{CHECKOUT_DISABLED_MESSAGE}</span> <a href={`/events/${slug}`}>Back to event</a></PageAlert>
        ) : (
          <form onSubmit={onContinue} noValidate aria-label="Ticket purchase" aria-busy={t.submitting || undefined}>
            {t.restored && <DraftRestoredBanner onDismiss={t.dismissRestored} />}
            {q && promoEnded && <PageAlert kind="warning">The promo price ended{promoEndText ? ` on ${promoEndText}` : ""}. Tickets are now {formatCents(q.unitPriceCents, q.currency)} each.</PageAlert>}
            {errorCount > 0 && <PageAlert kind="error">Please fix {errorCount} fields before continuing.</PageAlert>}
            {salesClosed && <PageAlert kind="info">{CHECKOUT_DISABLED_MESSAGE}</PageAlert>}
            {t.checkoutBlock === "rate_limited" && <PageAlert kind="warning">{CHECKOUT_RATE_LIMITED_MESSAGE}</PageAlert>}
            {t.attendeeTicketedMessage && <PageAlert kind="error">{t.attendeeTicketedMessage}{t.attendeeTicketedMessage === BUYER_ALREADY_TICKETED_MESSAGE && <> <a href={myTicketsTabHref(slug)} data-testid="cart-view-my-tickets">{VIEW_MY_TICKETS_LABEL}</a></>}</PageAlert>}
            {t.checkoutError && <PageAlert kind="error">We couldn't start checkout. Your details are saved. Try again.</PageAlert>}
            {t.unavailableReason && !cp.hideMessage(t.unavailableReason) && <PageAlert kind="warning">{t.unavailableReason}</PageAlert>}
            {t.quoteError && <PageAlert kind="error">We couldn't start checkout. Your details are saved. Try again.</PageAlert>}
            {t.holdPaid && <PageAlert kind="info">Payment was confirmed in another tab. This order is complete; this checkout can be closed.</PageAlert>}
            {t.soldOut && <PageAlert kind="info">This event is sold out. <a href={`/events/${slug}`}>Back to event</a></PageAlert>}

            <div className="tp-layout">
              <div className="tp-left">
                <BuyerCard
                  otpSlot={!isLoggedIn ? (
                    <>
                      {t.emailRecognition.status === "checking" && <p role="status">Checking this email…</p>}
                      {t.emailRecognition.status === "unknown" && <p role="alert">We couldn't confirm this email right now. Please check the address and try again.</p>}
                      {t.emailRecognition.signInRequired && (
                        <div role="status">
                          <p>This email uses an existing CHI Alliance sign-in. Continue through the current sign-in flow to buy tickets.</p>
                          <Link href={getLoginPath(`/events/${slug}/tickets`)} className="chi-btn-outline">Sign in to continue</Link>
                        </div>
                      )}
                      {t.emailRecognition.status === "member" && !t.emailRecognition.signInRequired && <p role="status">This email is connected to a member account. Sign in to continue.</p>}
                      {t.emailRecognition.status === "guest" && (
                        <div style={{ display: "grid", gap: "1rem", marginTop: "1rem" }}>
                          {([
                            ["companyName", "Company name", t.guestProfile.companyName],
                            ["jobTitle", "Job title", t.guestProfile.jobTitle],
                          ] as const).map(([field, label, value]) => (
                            <div key={field}>
                              <label className="tp-label" htmlFor={`buyer-${field}`}>{label}</label>
                              <input id={`buyer-${field}`} value={value} required autoComplete="organization" onChange={event => t.updateGuestField(field, event.target.value)} aria-invalid={t.guestFieldsSubmitted && Boolean(t.guestProfileErrors[field]) || undefined} aria-describedby={t.guestFieldsSubmitted && t.guestProfileErrors[field] ? `buyer-${field}-error` : undefined} style={inputStyle} />
                              {t.guestFieldsSubmitted && t.guestProfileErrors[field] && <p id={`buyer-${field}-error`} className="tp-error">{t.guestProfileErrors[field]}</p>}
                            </div>
                          ))}
                          <div>
                            <label className="tp-label" htmlFor="buyer-phone">Phone number</label>
                            <input id="buyer-phone" type="tel" inputMode="tel" autoComplete="tel" value={t.guestProfile.phone} required placeholder="(555) 555-5555" onChange={event => t.updateGuestField("phone", event.target.value)} aria-invalid={t.guestFieldsSubmitted && Boolean(t.guestProfileErrors.phone) || undefined} aria-describedby={t.guestFieldsSubmitted && t.guestProfileErrors.phone ? "buyer-phone-error" : undefined} style={inputStyle} />
                            {t.guestFieldsSubmitted && t.guestProfileErrors.phone && <p id="buyer-phone-error" className="tp-error">{t.guestProfileErrors.phone}</p>}
                          </div>
                        </div>
                      )}
                      {t.emailRecognition.status !== "checking" && t.emailRecognition.status !== "unknown" && !t.emailRecognition.signInRequired && (
                        <OtpInline email={t.rows[0].email} phase={t.otp.phase} prefill={t.otp.prefill} onSend={t.sendCode} onVerify={t.verifyCode} onChangeEmail={t.changeEmail} resendSeconds={OTP_RESEND_SECONDS} guestMode={t.emailRecognition.status === "guest"} disabled={t.emailRecognition.status === "idle"} />
                      )}
                      {!isLoggedIn && t.emailRecognition.status === "guest" && (
                        <section className="tp-join-invitation" aria-labelledby="ticket-join-after-checkout-heading" data-testid="ticket-join-invitation">
                          <label htmlFor="ticket-join-after-checkout">
                            <input
                              id="ticket-join-after-checkout"
                              type="checkbox"
                              checked={t.joinAfterCheckout}
                              onChange={event => t.setJoinAfterCheckout(event.target.checked)}
                            />
                            <span id="ticket-join-after-checkout-heading">Yes, I’d like to join for free after checkout.</span>
                          </label>
                          <p>Connect with fellow professionals, discover upcoming events, and stay involved beyond this event.</p>
                        </section>
                      )}
                    </>
                  ) : undefined}
                >
                  <PersonFields idPrefix="attendee-0" value={t.rows[0]} errors={t.emailRecognition.status === "guest" && t.guestFieldsSubmitted ? {
                    ...t.errors?.[0],
                    name: t.guestProfileErrors.name || t.errors?.[0]?.name,
                    email: t.guestProfileErrors.email || t.errors?.[0]?.email,
                  } : t.errors?.[0]} onChange={(f, v) => t.updateRow(0, f, v)} onBlur={t.validateOnBlur} autoCompleteBuyer readOnly={t.submitting} lockEmail={cp.lockBuyerEmail} />
                </BuyerCard>
                {t.rows.length > 1 && <section className="chi-ticket-card" aria-labelledby="tp-additional-heading">
                  <div className="tp-guest-list-title">
                    <h2 id="tp-additional-heading" style={{ fontSize: "1rem", color: "var(--tp-ink)" }}>Additional attendees</h2>
                    <span className="tp-guest-count">{t.rows.length - 1} {t.rows.length === 2 ? "guest" : "guests"}</span>
                  </div>
                  <p style={{ marginTop: 0, fontSize: ".82rem", color: "var(--tp-muted)" }}>Each attendee receives an individual ticket by email.</p>
                  {t.rows.slice(1).map((row, k) => {
                  const i = k + 1;
                  return (
                    <GuestCard key={i} index={i} showHelper={i === 1} onRemove={() => t.removeGuest(i)}>
                      <PersonFields idPrefix={`attendee-${i}`} label={`Guest ${i}`} value={row} errors={t.errors?.[i]} onChange={(f, v) => t.updateRow(i, f, v)} onBlur={t.validateOnBlur} readOnly={t.submitting} />
                    </GuestCard>
                  );
                  })}
                </section>}
              </div>
              <aside className="tp-aside" aria-label="Ticket selection and order">
                  {!t.quote && <section className="chi-ticket-card tp-checkout-summary" aria-label="Ticket selection">
                    {ticketHeader}
                    <div className="tp-loading-summary" role="status" aria-live="polite"><span className="tp-skeleton-line" /><span className="tp-skeleton-line" /><span className="tp-skeleton-line" /></div>
                  </section>}
                  {t.quote && <>
                  <OrderSummary
                    qty={t.quantity}
                    unitCents={t.quote.unitPriceCents}
                    fullCents={promoOn ? t.quote.fullPriceCents : null}
                    promoLabel={promoOn ? "Automatic promo" : null}
                    currency={t.promotionQuote?.currency ?? t.quote.currency}
                    promotionQuote={t.promotionQuote}
                    automaticSavingsCents={promoOn ? (t.quote.fullPriceCents - t.quote.unitPriceCents) * t.quantity : undefined}
                    ticketHeader={ticketHeader}
                  >
                    <PromotionCodeEntry
                      value={t.promotionInput}
                      quote={t.promotionQuote}
                      error={t.promotionError}
                      loading={t.promotionLoading}
                      needsAction={t.promotionNeedsAction}
                      disabled={t.submitting}
                      onChange={t.editPromotionCode}
                      onApply={t.applyPromotionCode}
                      onRemove={t.removePromotionCode}
                    />
                    {holdSlot ? <div className="tp-hold-note">{holdSlot}</div> : <p style={{ color: "var(--tp-muted)", fontSize: ".76rem", margin: ".8rem 0" }}>Ticket availability and final pricing are confirmed securely at checkout.</p>}
                    {t.holdExpired && <HoldExpiredNotice message={t.holdEndedMessage ?? undefined} onRetry={() => void onContinue()} />}
                    {cta}
                  </OrderSummary>
                  </>}
              </aside>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

/** Day before the exclusive end instant, in Chicago time ("Dec 31"). */
function formatPromoEnd(iso: string) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t - 1).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" });
}

function eventMetadata(event: CHIEvent) {
  const raw = event as CHIEvent & {
    eventDate?: string | Date | null;
    eventTime?: string | null;
    venue?: string | null;
    venueName?: string | null;
  };
  const dateValue = raw.eventDate ?? event.date;
  const date = formatEventDate(dateValue, event.timezone) ?? event.displayDate;
  const time = raw.eventTime ?? event.time;
  const venue = raw.venueName ?? raw.venue ?? event.location;
  return [date, time, venue].filter((part): part is string => Boolean(part?.trim())).join(" · ");
}

function formatEventDate(value: string | Date | null | undefined, timezone?: CHIEvent["timezone"]) {
  if (!value) return undefined;
  const dateOnly = typeof value === "string" ? value.match(/^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.000)?Z)?$/) : null;
  const date = value instanceof Date ? value : new Date(dateOnly ? `${dateOnly[1]}T12:00:00Z` : value);
  if (!Number.isFinite(date.getTime())) return undefined;
  const zone = timezone === "ET" ? "America/New_York"
    : timezone === "MT" ? "America/Denver"
      : timezone === "PT" ? "America/Los_Angeles"
        : "America/Chicago";
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: zone }).format(date);
}
